import * as React from 'react';
import { observable, action, computed } from 'mobx';
import { observer } from 'mobx-react';

import { Permissions } from '@app/AppConstants';
import { ContractPartTimeSplitType, ContractType, ContractTypeModel, PersonFunctionViewModel } from '@app/Models';
import { PersonContractViewModel } from '@app/Models/WebApiModels';

import { securityService } from '@app/Services/SecurityService';

import { ContractTypeModelDropdown } from './PersonContractTypeModelComponents/ContractTypeModelDropdown';
import { ContractPartTimeVariableDayBlock } from './PersonContractTypeModelComponents/ContractPartTimeVariableDayBlock';
import { IModalDialogContent, ModalWindow, ModalDialogOptions, ModalButtonType, ModalButtonOptions, modalService } from '@app/Components/Modal';
import { isCabinAt } from '@app/Pages/PersonInfo/Components/Helpers/personFunctionHelper';

type ChangeContractHandler = (contract: PersonContractViewModel) => Promise<boolean>;

type VariableDayTzvBlockProps = {
    contract: PersonContractViewModel;
    personFunctions: PersonFunctionViewModel[];
    onSave: ChangeContractHandler;
};

@observer
export class VariableDayTzvBlock extends React.Component<VariableDayTzvBlockProps, {}> implements IModalDialogContent<void> {
    private readonly _store: VariableDayTzvBlockStore;

    constructor(props: VariableDayTzvBlockProps) {
        super(props);
        this._store = new VariableDayTzvBlockStore(props);
    }

    public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
        const { hasPermission } = securityService;

        const buttons: ModalButtonOptions<void>[] = [{
            type: ModalButtonType.Cancel,
            onClick: () => {
                window.close();
            }
        }, {
            type: ModalButtonType.Save,
            isDisabled: !this._store.isValidForm || (!hasPermission(Permissions.EditObjectPersonContractTZVPart, true)),
            onClick: async () => {
                if (await this._store.onSave()) {
                    window.close(ModalButtonType.Save);
                }
            }
        }];

        const { contract } = this.props;
        if (contract.combinationVariableDayBlockLength && contract.combinationVariableDaySplitType) {
            buttons.push({
                type: ModalButtonType.Delete,
                color: 'danger',
                isDisabled: !hasPermission(Permissions.EditObjectPersonContractTZVPart, true),
                onClick: async () => {
                    if (await this._store.onDelete()) {
                        window.close();
                    }
                }
            });
        }

        return {
            title: 'Combine models',
            buttons: buttons,
            width: '350px'
        };
    }

    render() {
        const { hasPermission, permissionCss } = securityService;
        return (
            <>
                <ContractTypeModelDropdown
                    type={this._store.contractType}
                    isCabin={this._store.isCabin}
                    typeModel={this._store.typeModel}
                    onChange={() => void 0}
                    className={permissionCss(Permissions.ViewObjectPersonContractTZVPart, Permissions.EditObjectPersonContractTZVPart)}
                    disabled={true}
                />
                {(this._store.typeModel === ContractTypeModel.TZV_VariableDay || this._store.typeModel === ContractTypeModel.TZV_VariableDay_LhSff) &&
                    <ContractPartTimeVariableDayBlock
                        onChangeBlockLength={this._store.onChangePartTimeVariableDayBlockLength}
                        onChangeSplitType={this._store.onChangePartTimeVariableDaySplitType}
                        blockLength={this._store.combinationVariableDayBlockLength}
                        blockSplitType={this._store.combinationVariableDaySplitType}
                        className={permissionCss(Permissions.ViewObjectPersonContractTZVPart, Permissions.EditObjectPersonContractTZVPart)}
                        disabled={!hasPermission(Permissions.EditObjectPersonContractTZVPart, true)}
                        typeModel={this._store.typeModel}
                    />
                }
            </>
        );
    }
}

class VariableDayTzvBlockStore {
    @observable typeModel: ContractTypeModel = ContractTypeModel.TZV_VariableDay;
    @observable contract: PersonContractViewModel;
    @observable combinationVariableDayBlockLength?: number;
    @observable combinationVariableDaySplitType?: ContractPartTimeSplitType;
    @observable isCabin: boolean = false;

    private readonly _currentProps: VariableDayTzvBlockProps;

    constructor(props: VariableDayTzvBlockProps) {
        this._currentProps = props;
        this.updateProps(props);
        this.isCabin = isCabinAt(this._currentProps.personFunctions, this._currentProps.contract.dateFrom);
    }

    @action.bound
    async onSave() {
        if (this._currentProps.onSave) {
            const newContract = this.newContract();
            const result = await this._currentProps.onSave(newContract);
            if (result) {
                this.contract = newContract;
            }
            return result;
        }
        return false;
    }

    @action.bound
    async onDelete() {
        if (!await modalService.showConfirmation(`Do you really want to delete ${ContractTypeModel.TZV_VariableDay} model from ${this.contract.typeModel}?`, { width: '600px' })) {
            return;
        }

        this.combinationVariableDayBlockLength = undefined;
        this.combinationVariableDaySplitType = undefined;
        return await this.onSave();
    }

    newContract() {
        const contract: PersonContractViewModel = { ...this.contract };
        contract.combinationVariableDayBlockLength = this.combinationVariableDayBlockLength;
        contract.combinationVariableDaySplitType = this.combinationVariableDaySplitType;
        
        return contract;
    }

    @computed
    get isValidForm() {
        return this.combinationVariableDayBlockLength && this.combinationVariableDaySplitType;
    }

    @action.bound
    onChangePartTimeVariableDayBlockLength(newValue: number) {
        this.combinationVariableDayBlockLength = newValue;
    }

    @action.bound
    onChangePartTimeVariableDaySplitType(newValue: ContractPartTimeSplitType) {
        this.combinationVariableDaySplitType = newValue;
    }
    
    @computed
    get contractType() {
        return this.contract.type as ContractType;
    }

    @action
    updateProps(props: VariableDayTzvBlockProps) {
        this.contract = props.contract;
        this.combinationVariableDayBlockLength = props.contract.combinationVariableDayBlockLength;
        this.combinationVariableDaySplitType = props.contract.combinationVariableDaySplitType;
    }
}